import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Package, Search, Store, Truck } from 'lucide-react';
import { Button, Input, Skeleton, toast } from '@mlm/ui';
import { Decimal } from '@mlm/shared';
import { getStorefront, listMyOrders, productImageUrl, type OrderListRow } from '../lib/api';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { useCartStore } from '../storefront/lib/cart-store';
import { orderItemsToAddLines } from '../storefront/lib/cart-map';
import { toDisplay, useBrand } from '../storefront/lib/hooks';
import { usePricingSlotId } from '../lib/hooks/use-my-slots';
import { CopyButton } from '../components/copy-button';
import { fmtCurrency, fmtDateTime } from '../lib/format';

/**
 * Member Orders, Shopee-style: status tabs across the top, a search box, and
 * rich per-order cards (item thumbnails + names + qty + price, fulfillment
 * branch header, a friendly status line, and status-aware actions). Orders are
 * loaded once and filtered client-side by tab + search so switching tabs is
 * instant.
 */
type TabKey = 'all' | 'to_pay' | 'to_ship' | 'to_receive' | 'completed' | 'cancelled' | 'refund';

// Per-tab "seen" order ids, persisted so a badge only shows orders that have
// newly moved into a status since the member last opened that tab (notification
// style). Per-device (localStorage) — the bell notification is the cross-device
// signal.
const SEEN_KEY = 'mlm.member.orders.seen.v1';
function loadSeen(): Record<string, string[]> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(SEEN_KEY) ?? '{}');
  } catch {
    return {};
  }
}
function saveSeen(v: Record<string, string[]>) {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(v));
  } catch {
    /* ignore quota / private mode */
  }
}

const TABS: Array<{ key: TabKey; label: string; statuses: string[] | null }> = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'to_pay', label: 'To Pay', statuses: ['pending', 'to_pay'] },
  { key: 'to_ship', label: 'To Ship', statuses: ['paid', 'to_ship', 'to_release'] },
  { key: 'to_receive', label: 'To Receive', statuses: ['shipping', 'delivered', 'to_pick'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
  { key: 'refund', label: 'Return/Refund', statuses: ['refunded'] },
];

export function OrdersPage() {
  const { activeSlotId } = useMySlots();
  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');

  // Slot-scoped: orders under the active slot PLUS the member's un-slotted
  // orders (kit/pre-activation buys carry no buyerSlotId, so they'd otherwise
  // never appear). The OR-null inclusion lives in the API.
  const q = useQuery({
    queryKey: ['orders', activeSlotId ?? 'all'],
    queryFn: () => listMyOrders(activeSlotId ?? undefined),
  });

  const orders = q.data ?? [];
  const [seen, setSeen] = useState<Record<string, string[]>>(loadSeen);

  // Opening a tab marks every order currently in it as "seen" for that tab, so
  // its badge clears. A later order moving into the tab won't be in the seen set
  // → the badge reappears with just the new count.
  useEffect(() => {
    const tabDef = TABS.find((t) => t.key === tab)!;
    const idsInTab = orders
      .filter((o) => tabDef.statuses === null || tabDef.statuses.includes(o.status))
      .map((o) => o.id);
    setSeen((prev) => {
      const prevIds = prev[tab] ?? [];
      const same =
        prevIds.length === idsInTab.length && idsInTab.every((id) => prevIds.includes(id));
      if (same) return prev;
      const next = { ...prev, [tab]: idsInTab };
      saveSeen(next);
      return next;
    });
  }, [tab, orders]);

  // Per-tab badge = orders in that group the member hasn't seen yet.
  const counts = useMemo(() => {
    const c = Object.fromEntries(TABS.map((t) => [t.key, 0])) as Record<TabKey, number>;
    for (const o of orders) {
      for (const t of TABS) {
        if (t.key === 'all') continue;
        if (t.statuses !== null && !t.statuses.includes(o.status)) continue;
        if (!(seen[t.key] ?? []).includes(o.id)) c[t.key] += 1;
      }
    }
    return c;
  }, [orders, seen]);
  const filtered = useMemo(() => {
    const tabDef = TABS.find((t) => t.key === tab)!;
    const term = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (tabDef.statuses && !tabDef.statuses.includes(o.status)) return false;
      if (!term) return true;
      if (o.code.toLowerCase().includes(term)) return true;
      if (o.fulfillmentBranch?.name.toLowerCase().includes(term)) return true;
      return (o.items ?? []).some((it) => it.productName.toLowerCase().includes(term));
    });
  }, [orders, tab, search]);

  return (
    <div className="space-y-4">
      {/* Tab bar — horizontally scrollable on small screens. */}
      <div className="-mx-1 overflow-x-auto">
        <div className="flex min-w-max border-b">
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative whitespace-nowrap px-4 py-2.5 text-sm transition-colors ${
                  active
                    ? 'font-medium text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
                {t.key !== 'all' && counts[t.key] > 0 && (
                  <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
                    {counts[t.key]}
                  </span>
                )}
                {active && (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by Order ID or product name"
          className="pl-9"
        />
      </div>

      {/* List */}
      {q.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasOrders={orders.length > 0} />
      ) : (
        <div className="space-y-3">
          {groupOrders(filtered).map((g) =>
            g.kind === 'group' ? (
              <OrderGroupCard key={g.id} orders={g.orders} />
            ) : (
              <OrderCard key={g.order.id} o={g.order} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ hasOrders }: { hasOrders: boolean }) {
  return (
    <div className="rounded-lg border bg-card py-16 text-center">
      <Package className="mx-auto h-10 w-10 text-muted-foreground/50" />
      <p className="mt-3 text-sm text-muted-foreground">
        {hasOrders ? 'No orders in this status.' : 'No orders yet.'}
      </p>
      {!hasOrders && (
        <Button asChild size="sm" className="mt-4">
          <Link to="/storefront">Browse the store</Link>
        </Button>
      )}
    </div>
  );
}

/**
 * Collapse orders that share a `checkoutGroupId` (mixed-cart single payment)
 * into one group entry, preserving the original list order (a group takes the
 * slot of its first member). Orders with no group, or a group with only one
 * member in the current (filtered) set, render standalone.
 */
type GroupedEntry =
  | { kind: 'single'; order: OrderListRow }
  | { kind: 'group'; id: string; orders: OrderListRow[] };

function groupOrders(orders: OrderListRow[]): GroupedEntry[] {
  const counts = new Map<string, number>();
  for (const o of orders) {
    if (o.checkoutGroupId) counts.set(o.checkoutGroupId, (counts.get(o.checkoutGroupId) ?? 0) + 1);
  }
  const out: GroupedEntry[] = [];
  const emitted = new Set<string>();
  for (const o of orders) {
    const gid = o.checkoutGroupId;
    if (gid && (counts.get(gid) ?? 0) > 1) {
      if (emitted.has(gid)) continue;
      emitted.add(gid);
      out.push({ kind: 'group', id: gid, orders: orders.filter((x) => x.checkoutGroupId === gid) });
    } else {
      out.push({ kind: 'single', order: o });
    }
  }
  return out;
}

/** Item thumbnails + name/qty/price for one order. */
function OrderItemList({ order }: { order: OrderListRow }) {
  const items = order.items ?? [];
  return (
    <ul>
      {items.length === 0 ? (
        <li className="px-4 py-4 text-sm text-muted-foreground">
          {order.code} · {fmtDateTime(order.createdAt)}
        </li>
      ) : (
        items.map((it, idx) => {
          const hasImg = it.productImages && it.productImages.length > 0;
          const discounted = Number(it.unitPriceBase) > Number(it.unitPrice);
          return (
            <li key={idx} className="flex gap-3 px-4 py-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
                {hasImg ? (
                  <img
                    src={productImageUrl(it.productId, 0)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <ImageOff className="h-5 w-5 text-muted-foreground" aria-hidden />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-sm">
                  {it.productName}
                  {it.variantName ? ` — ${it.variantName}` : ''}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">x{it.qty}</div>
              </div>
              <div className="shrink-0 text-right text-sm">
                {discounted && (
                  <div className="text-xs text-muted-foreground line-through tabular-nums">
                    {fmtCurrency(it.unitPriceBase)}
                  </div>
                )}
                <div className="tabular-nums">{fmtCurrency(it.unitPrice)}</div>
              </div>
            </li>
          );
        })
      )}
    </ul>
  );
}

/**
 * Tracking strip — clickable when the courier link is known, a copy button so
 * the number can be pasted into the courier site, plus an explicit "Track
 * Package" button so members don't have to realize the number itself is a link.
 */
function TrackingStrip({ order }: { order: OrderListRow }) {
  if (!order.trackingNumber) return null;
  return (
    <div className="flex items-center gap-1.5 border-t bg-muted/10 px-4 py-2 text-xs">
      <Truck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="text-muted-foreground">Tracking #:</span>
      {order.trackingUrl ? (
        <a
          href={order.trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-mono font-medium text-primary underline underline-offset-2"
        >
          {order.trackingNumber}
        </a>
      ) : (
        <span className="font-mono font-medium">{order.trackingNumber}</span>
      )}
      <CopyButton
        value={order.trackingNumber}
        label="Tracking number copied"
        title="Copy tracking number"
      />
      {order.trackingUrl && (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="ml-auto h-7 gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer">
            <Truck className="h-3.5 w-3.5" aria-hidden />
            Track Package
          </a>
        </Button>
      )}
    </div>
  );
}

function OrderCard({ o }: { o: OrderListRow }) {
  const navigate = useNavigate();
  const add = useCartStore((s) => s.add);
  const brand = useBrand();
  const pricingSlotId = usePricingSlotId();
  const [reordering, setReordering] = useState(false);
  const items = o.items ?? [];
  // Proof submitted, waiting on admin to verify — distinct from "to pay".
  const verifying = (o.status === 'pending' || o.status === 'to_pay') && o.paymentPending;
  const badge = verifying
    ? {
        label: 'VERIFYING',
        cls: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
      }
    : statusBadge(o.status);
  const statusLine = verifying ? 'Awaiting payment verification' : statusText(o.status);
  const action = verifying ? null : primaryAction(o.status);

  /**
   * Re-add this order's lines to the storefront cart and go pay.
   *
   * Re-priced from the LIVE catalog rather than replayed at the old price, and
   * VARIANT-aware: the previous version re-added by product id alone, which
   * dropped the variation and made the server price and stock-check the parent.
   * Lines whose product or variation has since left the catalog are dropped and
   * called out rather than silently missing.
   */
  async function buyAgain() {
    setReordering(true);
    try {
      const catalog = (
        await getStorefront(undefined, undefined, pricingSlotId ?? undefined)
      ).products.map(toDisplay);
      const { lines, missingCount } = orderItemsToAddLines(items, catalog, brand);
      if (lines.length === 0) {
        toast.error('Those items are no longer available.');
        return;
      }
      lines.forEach(add);
      if (missingCount > 0) {
        toast.info(
          `${missingCount} item${missingCount === 1 ? ' is' : 's are'} no longer available and ${
            missingCount === 1 ? 'was' : 'were'
          } left out.`,
        );
        // Let them see what actually made it in before paying.
        navigate('/cart');
        return;
      }
      navigate('/checkout');
    } catch {
      toast.error("Couldn't load the store right now. Please try again.");
    } finally {
      setReordering(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {/* Header: fulfillment branch (or store) + status. */}
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">
            {o.fulfillmentBranch?.name ?? 'Store'}
          </span>
          {o.fulfillmentBranch && (
            <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
              ({o.fulfillmentBranch.code})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden text-xs text-muted-foreground sm:inline">{statusLine}</span>
          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* Items — clicking opens the order detail. */}
      <Link to={`/orders/${o.id}`} className="block hover:bg-muted/40">
        <OrderItemList order={o} />
      </Link>

      <TrackingStrip order={o} />

      {/* Footer: total + actions. */}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t bg-muted/20 px-4 py-3">
        <div className="mr-auto text-xs text-muted-foreground">
          Order Ref. Number: <span className="font-mono">{o.code}</span> ·{' '}
          {fmtDateTime(o.createdAt)}
        </div>
        <div className="text-sm">
          Order Total:{' '}
          <span className="text-base font-semibold tabular-nums text-primary">
            {fmtCurrency(o.total)}
          </span>
        </div>
        <div className="flex w-full justify-end gap-2 sm:w-auto">
          {action?.kind === 'buyagain' ? (
            <Button size="sm" onClick={buyAgain} disabled={items.length === 0 || reordering}>
              {reordering ? 'Adding…' : 'Buy again'}
            </Button>
          ) : action ? (
            <Button asChild size="sm">
              <Link to={`/orders/${o.id}`}>{action.label}</Link>
            </Button>
          ) : null}
          <Button asChild size="sm" variant="outline">
            <Link to={`/orders/${o.id}`}>View order</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mixed-cart single payment: every order in the group was paid (or is payable)
 * with ONE proof, so they're shown as one merged card — one section per shop,
 * a combined total, and a single "Pay now" that opens the group payment flow
 * (the order-detail page detects `checkoutGroupId` and pays the whole group).
 */
function OrderGroupCard({ orders }: { orders: OrderListRow[] }) {
  const total = orders.reduce((s, o) => s.plus(new Decimal(o.total)), new Decimal(0));
  const statuses = new Set(orders.map((o) => o.status));
  const uniform = statuses.size === 1 ? statusBadge(orders[0]!.status) : null;
  const unpaid = orders.find((o) => o.status === 'pending' || o.status === 'to_pay');

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {/* Group header: single-payment banner + uniform status (if all match). */}
      <div className="flex items-center justify-between gap-3 border-b bg-muted/10 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">
            {orders.length} shops · single payment
          </span>
        </div>
        {uniform && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${uniform.cls}`}>
            {uniform.label}
          </span>
        )}
      </div>

      {/* One section per shop. */}
      <div className="divide-y">
        {orders.map((o) => {
          const badge = statusBadge(o.status);
          return (
            <div key={o.id}>
              <div className="flex items-center justify-between gap-3 px-4 pt-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">
                    {o.fulfillmentBranch?.name ?? 'Store'}
                  </span>
                  {o.fulfillmentBranch && (
                    <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
                      ({o.fulfillmentBranch.code})
                    </span>
                  )}
                </div>
                {!uniform && (
                  <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${badge.cls}`}>
                    {badge.label}
                  </span>
                )}
              </div>
              <Link to={`/orders/${o.id}`} className="block hover:bg-muted/40">
                <OrderItemList order={o} />
              </Link>
              <TrackingStrip order={o} />
            </div>
          );
        })}
      </div>

      {/* Footer: combined total + a single Pay now (when anything is unpaid). */}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t bg-muted/20 px-4 py-3">
        <div className="mr-auto text-xs text-muted-foreground">
          Single payment · {orders.length} orders
        </div>
        <div className="text-sm">
          Total:{' '}
          <span className="text-base font-semibold tabular-nums text-primary">
            {fmtCurrency(total.toFixed(2))}
          </span>
        </div>
        {unpaid && (
          <Button asChild size="sm">
            <Link to={`/orders/${unpaid.id}`}>Pay now</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

function statusText(status: string): string {
  switch (status) {
    case 'pending':
    case 'to_pay':
      return 'Awaiting payment';
    case 'paid':
    case 'to_ship':
      return 'Preparing to ship';
    case 'to_release':
      return 'Ready for release';
    case 'shipping':
      return 'On the way';
    case 'delivered':
      return 'Parcel has been delivered';
    case 'to_pick':
      return 'Ready for pickup';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'refunded':
      return 'Refunded';
    default:
      return status.replace(/_/g, ' ');
  }
}

function statusBadge(status: string): { label: string; cls: string } {
  const amber = 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
  const blue = 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300';
  const green = 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300';
  const red = 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
  switch (status) {
    case 'pending':
    case 'to_pay':
      return { label: 'TO PAY', cls: amber };
    case 'paid':
    case 'to_ship':
    case 'to_release':
      return { label: 'TO SHIP', cls: blue };
    case 'shipping':
    case 'delivered':
    case 'to_pick':
      return { label: 'TO RECEIVE', cls: blue };
    case 'completed':
      return { label: 'COMPLETED', cls: green };
    case 'cancelled':
      return { label: 'CANCELLED', cls: red };
    case 'refunded':
      return { label: 'REFUND', cls: red };
    default:
      return { label: status.toUpperCase(), cls: 'bg-muted text-muted-foreground' };
  }
}

/** Status-aware primary button. `null` = no primary (just "View order"). */
function primaryAction(status: string): { label: string; kind: 'link' | 'buyagain' } | null {
  switch (status) {
    case 'pending':
    case 'to_pay':
      return { label: 'Pay now', kind: 'link' };
    case 'delivered':
    case 'to_pick':
      return { label: 'Mark received', kind: 'link' };
    case 'completed':
      return { label: 'Buy again', kind: 'buyagain' };
    default:
      return null;
  }
}
